import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useModals } from "@/context/ModalContext";
import { api } from "@/lib/api";
import { GOOGLE_MEET_LINK, PHONE, PHONE_HREF } from "@/lib/config";
import { Calendar, Check, Phone } from "lucide-react";
import { toast } from "sonner";

const field = "w-full bg-charcoal border border-white/15 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold placeholder:text-cream/40";

export const ConsultationModal = () => {
  const { consultation, closeConsultation } = useModals();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const open = !!consultation;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.postConsultation({ ...form, productInterest: consultation?.productInterest || "" });
      setDone(true);
    } catch (e) { toast.error("Something went wrong."); }
  };
  const close = () => { closeConsultation(); setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", message: "" }); }, 300); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="consultation-modal" aria-describedby={undefined} className="bg-espresso border-white/10 text-cream sm:max-w-md">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-cream">Talk to a Sauna Expert</DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-cream/60 text-sm -mt-1">Free 1-on-1 guidance from our recovery specialists.</DialogDescription>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <input data-testid="consult-name" required placeholder="Full name" className={field}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="consult-email" required type="email" placeholder="Email" className={field}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="consult-phone" placeholder="Phone (optional)" className={field}
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              {consultation?.productInterest && (
                <input readOnly value={consultation.productInterest} className={field + " opacity-70"} />
              )}
              <textarea data-testid="consult-message" placeholder="What would you like help with?" rows={3} className={field}
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button data-testid="consult-submit" type="submit"
                className="w-full bg-gold text-espresso py-3 font-medium hover:bg-amber transition-colors">Request Consultation</button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="consult-success">
            <div className="w-14 h-14 rounded-full bg-forest/20 flex items-center justify-center mx-auto mb-4">
              <Check className="text-forest" size={28} />
            </div>
            <h3 className="font-serif text-2xl">You're all set, {form.name.split(" ")[0]}.</h3>
            <p className="text-cream/65 text-sm mt-2">Book a time that works for you, or we'll reach out within one business day.</p>
            <a href={GOOGLE_MEET_LINK} target="_blank" rel="noreferrer" data-testid="consult-book-link"
              className="mt-5 inline-flex items-center gap-2 bg-gold text-espresso px-6 py-3 font-medium hover:bg-amber transition-colors">
              <Calendar size={18} /> Book Your Call
            </a>
            <a href={PHONE_HREF} className="mt-3 flex items-center justify-center gap-2 text-cream/70 hover:text-gold text-sm">
              <Phone size={14} /> Or call {PHONE}
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
